import { PRIME_NG_CONFIG, PrimeNG, PrimeNGConfigType } from 'primeng/config';
import { NgKitTheme } from './ngkit-theme';
import {
  CSP_NONCE,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
  Provider,
  NgZone,
} from '@angular/core';

/**
 * Provide NgKit theme configuration for Angular applications.
 * Sets up PrimeNG theme integration with CSP nonce, preset, CSS layers, and dark mode support.
 * CSP Nonce Injection:
 * The function automatically injects CSP_NONCE from Angular's dependency injection system.
 * This is used to secure inline styles added by PrimeNG when Content Security Policy headers
 * restrict inline CSS. The nonce is provided by your application's security infrastructure.
 * Integration Requirements:
 * 1. Provide CSP_NONCE token in your application bootstrap:
 *    ```typescript
 *    const nonce = document.querySelector('script[nonce]')?.getAttribute('nonce');
 *    bootstrapApplication(AppComponent, {
 *      providers: [
 *        provideNgKitTheme(),
 *        { provide: CSP_NONCE, useValue: nonce }
 *      ]
 *    });
 *    ```
 * 2. Set CSP headers on your server:
 *    ```
 *    Content-Security-Policy: style-src 'nonce-{random-value}'
 *    ```
 * 3. Ensure HTML script tags include nonce attribute:
 *    ```html
 *    <script nonce="{random-value}">...</script>
 *    ```
 * Dark Mode:
 * Dark mode is activated by adding the 'ngkit-dark' class to a parent element (typically <html>).
 * @param overrides - Optional partial configuration overrides for PrimeNG theme settings
 * @returns Environment providers for application bootstrapping
 * @example
 * ```typescript
 * // In main.ts with CSP support
 * const nonce = document.querySelector('script[nonce]')?.getAttribute('nonce');
 * bootstrapApplication(AppComponent, {
 *   providers: [
 *     provideNgKitTheme({ theme: { options: { darkModeSelector: '.my-dark-class' } } }),
 *     { provide: CSP_NONCE, useValue: nonce }
 *   ]
 * });
 * ```
 */
export function provideNgKitTheme(overrides?: Partial<PrimeNGConfigType>): EnvironmentProviders {
  // We replace the providePrimeNG utility function entirely, as we want to inject the CSP_NONCE directly.
  // This requires a factory provider which is incompatible with the default implementation.
  const themeProvider: Provider = {
    provide: PRIME_NG_CONFIG,
    useFactory: () => ({
      csp: {
        nonce: inject(CSP_NONCE) ?? undefined,
      },
      theme: {
        preset: NgKitTheme,
        options: {
          cssLayer: {
            name: 'primeng',
            order: 'primeng',
          },
          darkModeSelector: '.ngkit-dark',
        },
      },
      ...overrides,
    }),
  };

  return makeEnvironmentProviders([
    themeProvider,
    provideAppInitializer(() => {
      const primeNG = inject(PrimeNG);
      const zone = inject(NgZone);

      // Set config within Angular zone to ensure proper change detection
      zone.run(() => {
        primeNG.setConfig(inject(PRIME_NG_CONFIG));
      });
    }),
  ]);
}
